import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { afterLayout } from '@theme/custom.after-layout';

// Product cards plus any inline promo tiles, in document order so a tile injected mid-page lands
// in its slot. The append path and the cell count share it: both have to agree what fills a cell.
const GRID_ITEM_SELECTOR = ':scope > [ref="cards[]"], :scope > .product-grid__promo-item';

const TAIL_TOP_UP_TIMEOUT = 500;

/**
 * An idle callback that is guaranteed to run. A bare one has no deadline, so a PLP still decoding a
 * screenful of images can starve it past the point the shopper has already seen the gap.
 *
 * Not the shared helper in utilities.js: its type is the union of requestIdleCallback and
 * setTimeout, which leaves no second argument able to satisfy both, so no deadline can be passed.
 *
 * @param {() => void} callback
 */
function whenIdle(callback) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(callback, { timeout: TAIL_TOP_UP_TIMEOUT });
  } else {
    setTimeout(callback, 0);
  }
}

// Load more never touches the URL, so Back (often a bfcache miss) re-renders only page one. Store
// how far the shopper loaded and where they were, and restore it when they come back.
const POSITION_STORAGE_PREFIX = 'plp-position:';

// Older than this, a stored position is stale enough that landing on it would surprise, not help.
const POSITION_STORAGE_TTL = 30 * 60 * 1000;

// One listing URL can sit at several history entries, so a position is keyed by this per-entry id
// in `history.state`. An entry without one (the cart drawer pushes those) falls back to the URL.
const POSITION_ENTRY_KEY = 'plpEntryId';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLElement} [loadMore] - Wrapper around the Load more control, empty on the
 *   last page.
 * @property {HTMLElement} [loadMoreStatus] - Live region announcing what a press added.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #loading = false;

  /** @type {{ document: Document, items: Element[] } | undefined} */
  #nextPageBuffer;

  /** @type {boolean} */
  #tailTopUpScheduled = false;

  /** @type {Promise<void> | null} */
  #tailTopUp = null;

  /** @type {boolean} */
  #tailTopUpOwed = false;

  /** @type {boolean} */
  #nextPageDropped = false;

  /** @type {boolean} */
  #tailTopUpRetried = false;

  /** @type {number} */
  #listGeneration = 0;

  /** @type {(() => void) | undefined} */
  #stopColumnWatch;

  /** @type {number | undefined} */
  #columnCountAtLastTopUp;

  /**
   * A back/forward rebuild in flight. A press waits on it the same way it waits on a top-up, so a
   * click landing mid-rebuild is honoured rather than dropped.
   *
   * @type {Promise<void> | null}
   */
  #restore = null;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    // Numbered pages navigate, so nothing here can consume a prefetch: skipping it saves two
    // Section Rendering round trips per view.
    if (this.paginationMode !== 'pages') {
      this.#fetchPage('next');
      this.#fetchPage('previous');
    }

    // The server page can end mid-row (16 products plus a 2-cell promo tile is 18, two short of a
    // 4-column row), and a gap above the button reads as the end of the results.
    if (this.paginationMode === 'load_more') {
      this.#scheduleTailTopUp();
      this.#observeColumnCount();
    }

    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    // Load more never changes the URL, so a fresh Back cannot rebuild the appended list on its
    // own. Record the shopper's place as they leave, and put it back when they return.
    if (this.paginationMode === 'load_more') {
      window.addEventListener('pagehide', this.#savePosition);
      // Restore before stamping, so a transient entry with no id of its own falls back to the URL
      // token on read instead of being handed a fresh id that would miss the saved key.
      this.#restorePosition();
      this.#stampEntry();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.#stopColumnWatch?.();
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.removeEventListener('pagehide', this.#savePosition);
  }

  /**
   * How the shopper reaches the next page, mirroring the section setting. An absent attribute
   * resolves to "infinite", the mode a subclass that sets none (the blog list) runs in.
   *
   * @returns {'load_more' | 'infinite' | 'pages'}
   */
  get paginationMode() {
    const mode = this.getAttribute('pagination-mode');

    return mode === 'load_more' || mode === 'pages' ? mode : 'infinite';
  }

  /**
   * Appends the next page from the Load more control. That control is a real link to the page, so
   * its navigation has to be suppressed here; without JS the link is the whole feature.
   *
   * @param {MouseEvent} event
   */
  async loadMore(event) {
    event.preventDefault();

    // A back/forward rebuild owns the grid while it restores the loaded pages. A press arriving
    // then waits it out rather than being dropped, just as it does for a top-up below.
    if (this.#restore) {
      this.refs.loadMore?.setAttribute('aria-busy', 'true');
      await this.#restore;
    }

    // A top-up can hold #loading with no press behind it. Wait for it rather than return: the link
    // is already prevented, so a dropped press would do nothing. A second press still bails.
    if (this.#loading && this.#tailTopUp) {
      // Set before the wait, not after: a press parked on someone else's fetch is not an idle
      // button, and the press that takes over from here clears it in its own finally.
      this.refs.loadMore?.setAttribute('aria-busy', 'true');
      await this.#tailTopUp;
    }

    if (this.#loading) return;

    this.#loading = true;
    this.refs.loadMore?.setAttribute('aria-busy', 'true');

    try {
      if (this.paginationMode === 'load_more') {
        // Promo tiles are not something the shopper asked to see more of, so they go uncounted.
        this.#announceLoadedItems(await this.#renderNextRows());
      } else {
        await this.#renderNextPage();
      }
    } finally {
      this.#loading = false;
      this.refs.loadMore?.removeAttribute('aria-busy');
      this.#releaseTailTopUp();
    }
  }

  /**
   * Squares the tail once the press that owned the grid lets go. Two things leave one ragged: a row
   * the press could not close, because the page it had to reach into never arrived, and a column
   * change the observer saw while the press held the flag.
   *
   * Both say so themselves: the page that never arrived sets the dropped flag, and the observer's
   * request is the one this press was owed. Neither needs the whole grid measured again.
   *
   * One attempt per press. A tail still ragged after it is either the last page, where a short row
   * is correct, or a second dropped request; the top-up's own retry picks that up.
   */
  #releaseTailTopUp() {
    if (this.paginationMode !== 'load_more') return;

    const owed = this.#tailTopUpOwed;
    const dropped = this.#nextPageDropped;
    this.#tailTopUpOwed = false;
    this.#nextPageDropped = false;

    if (!owed && !dropped) return;

    this.#scheduleTailTopUp();
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (error) {
      // Swallowed on purpose, twice over: the waiter below has to be released or the control that
      // asked for this page stays disabled for good, and most callers here are fire-and-forget.
      console.error(error);
    } finally {
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    const generation = this.#listGeneration;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);

    // A filter emptied the cache while this was in flight, so this answers the listing the shopper
    // has left. `pages` is keyed by number, so writing it in hands the new list the old products.
    if (generation !== this.#listGeneration) return;

    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = await this.#nextPageDocument();
    if (!nextPage) return;

    const nextPageItemElements = this.#getGridItems(nextPage.document);
    if (!nextPageItemElements) return;

    grid.append(...nextPageItemElements);

    // Only built when a card gallery is in the list, and a no-op outside the theme editor.
    this.#aspectRatioHelper?.processNewElements();

    // Load more leaves pages 1..N on screen at once, so pinning the URL to page N would strand the
    // earlier pages on reload. Only the scrolling mode, which back-fills upwards, can deep-link.
    if (this.paginationMode === 'infinite') {
      history.pushState('', '', nextPage.url.toString());
    }

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  /**
   * Appends `extent` past the current tail, then on to the next row boundary, so the grid never
   * ends on a gap that reads as the end of the results. 0 closes the current row and no more.
   *
   * `'page'` is what a press asks for: the rest of the next server page, which is one quantity at
   * every breakpoint and is the store's own paginate size, never a number in this file.
   *
   * The ROUNDING still counts cells, not products: a promo tile takes two of them and the organic
   * layout hands two to some cards, so no product count closes a row at every breakpoint.
   *
   * @param {number | 'page'} extent
   * @returns {Promise<number>} Products appended, so a silent top-up can skip the announcement.
   */
  async #renderNextRows(extent = 'page') {
    // Cleared per run, not per page request: a run that never asks for a page (no grid, a grid
    // already square, a press served out of the buffer) must not be released on an older answer.
    this.#nextPageDropped = false;

    const { grid } = this.refs;
    if (!grid) return 0;

    const columns = this.#columnCount(grid);
    let cells = this.#cellsFor(grid.querySelectorAll(GRID_ITEM_SELECTOR), columns);
    // Unbounded for a page: only #takeItems knows how much of one is left, so the target is pinned
    // below once the batch is in hand and the loop carries on to close the row it ended on.
    let target = extent === 'page' ? Infinity : cells + columns * extent;
    let appended = 0;
    let products = 0;

    while (cells < target || cells % columns !== 0) {
      // Asked for as one batch, sized as if every item took a single cell. A wider item overshoots
      // the row, and the next turn closes it; appending one at a time would flush layout per card.
      const deficit = cells < target ? target - cells : columns - (cells % columns);
      const batch = await this.#takeItems(deficit);
      if (!batch.length) break;

      grid.append(...batch);
      cells += this.#cellsFor(batch, columns);
      if (target === Infinity) target = cells;
      appended += batch.length;
      products += batch.filter((item) => item.matches('[ref="cards[]"]')).length;
    }

    if (!appended) return 0;

    this.#aspectRatioHelper?.processNewElements();

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });

    return products;
  }

  /**
   * Closes a ragged tail, silently and off the critical path. A page-sized press does not retire
   * it: `templates/*.json` is deploy-ignored, so the page size is never a number here, and no one
   * page size lands flush at 2, 3 and 4 columns on every page anyway.
   */
  #scheduleTailTopUp() {
    if (this.#tailTopUpScheduled) return;
    this.#tailTopUpScheduled = true;

    whenIdle(() => {
      this.#tailTopUpScheduled = false;
      // Kept, not dropped: #observeColumnCount has already recorded the new column count, so it
      // will not fire for that count again, and dropping it here left a phone's turn permanent.
      if (this.#loading) {
        this.#tailTopUpOwed = true;
        return;
      }

      this.#loading = true;
      // Held so a press arriving mid-flight can await it. Never rejects: squaring the grid is
      // cosmetic and must not take a shopper's press down with it.
      this.#tailTopUp = (async () => {
        try {
          // 0 rows: this is not a request for more products, only for the row already on screen to
          // finish. The last page has none left to give and correctly stays as it is.
          await this.#renderNextRows(0);
        } catch (error) {
          console.error(error);
        } finally {
          this.#loading = false;
          this.#tailTopUp = null;
          // Raggedness alone is no reason to run again: a row this could not close is the last
          // page, and re-running on that would schedule itself for ever.
          if (this.#tailTopUpOwed) {
            this.#tailTopUpOwed = false;
            this.#scheduleTailTopUp();
          } else if (this.#nextPageDropped && !this.#tailTopUpRetried) {
            // One shot for the life of the list, or a page that fails every time is that same loop.
            this.#tailTopUpRetried = true;
            this.#scheduleTailTopUp();
          }
        }
      })();
    });
  }

  /**
   * A breakpoint change re-cuts the rows, so a tail that sat flush at 3 columns is two short at 4.
   * Gating on a real change in column count is also what stops the top-up's own appends from
   * feeding this back: they change the grid's height, never how many columns it has.
   */
  #observeColumnCount() {
    if (!this.refs.grid || typeof ResizeObserver === 'undefined') return;

    // Watches the host, which survives a filter morph, and reads the grid on screen now. The first
    // count is the baseline, read after the first frame's layout, so connect forces none.
    this.#stopColumnWatch = afterLayout(
      this,
      () => {
        const { grid } = this.refs;
        return grid ? this.#columnCount(grid) : undefined;
      },
      (columns) => {
        if (columns === undefined) return;
        const baseline = this.#columnCountAtLastTopUp;
        this.#columnCountAtLastTopUp = columns;
        if (baseline !== undefined && columns !== baseline) this.#scheduleTailTopUp();
      }
    );
  }

  /**
   * Up to `count` items, and never from more than ONE page. #getPage reads the next page number off
   * the last card in the live grid, and the caller appends only after this returns, so a call that
   * drained two pages would ask for the page it had just consumed and append it twice.
   *
   * A page is left part-consumed when its items straddle the row boundary; the rest wait.
   *
   * @param {number} count
   * @returns {Promise<Element[]>}
   */
  async #takeItems(count) {
    if (!this.#nextPageBuffer) {
      const nextPage = await this.#nextPageDocument();
      if (!nextPage) return [];

      const items = this.#getGridItems(nextPage.document);
      if (!items?.length) return [];

      this.#nextPageBuffer = { document: nextPage.document, items: [...items] };
    }

    const buffer = this.#nextPageBuffer;
    const taken = buffer.items.splice(0, count);

    // The control has to advance the moment its page is spent, or the next press reads a link to
    // the page already on screen.
    if (!buffer.items.length) {
      this.#syncLoadMore(buffer.document);
      this.#nextPageBuffer = undefined;
    }

    return taken;
  }

  /**
   * The next page, and on the way out which kind of nothing it is: a page that dropped, or a spent
   * catalogue. #takeItems reports both as an empty array, so this flag is what tells them apart.
   *
   * @returns {Promise<{ document: Document, page: number, url: URL } | undefined>}
   */
  async #nextPageDocument() {
    const generation = this.#listGeneration;
    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    let document = this.#parsePage(nextPage.page);
    if (!document) {
      const promise = new Promise((resolve) => {
        this.#resolveNextPagePromise = resolve;
      });

      void this.#fetchPage('next');
      await promise;
      document = this.#parsePage(nextPage.page);
    }

    if (!document) {
      // A filter update empties the cache and releases this waiter on purpose, so a page missing
      // under a newer list is that unblock rather than a request that dropped.
      this.#nextPageDropped = generation === this.#listGeneration;
      return;
    }

    return { document, ...nextPage };
  }

  /** @param {HTMLUListElement} grid */
  #columnCount(grid) {
    const template = getComputedStyle(grid).gridTemplateColumns;
    return template && template !== 'none' ? template.trim().split(/\s+/).length : 1;
  }

  /** @param {Element} item @param {number} columns */
  #columnSpan(item, columns) {
    const style = getComputedStyle(item);
    const match = [style.gridColumnStart, style.gridColumnEnd]
      .map((line) => line.match(/^span\s+(\d+)$/))
      .find(Boolean);
    return Math.min(match ? Number(match[1]) : 1, columns);
  }

  /**
   * The cells `items` occupy. Read in one pass with no append in between, so a whole batch costs a
   * single style flush instead of one per item.
   *
   * @param {Iterable<Element>} items
   * @param {number} columns
   */
  #cellsFor(items, columns) {
    let total = 0;
    for (const item of items) total += this.#columnSpan(item, columns);
    return total;
  }

  /** @param {number} count */
  #announceLoadedItems(count) {
    const { loadMore, loadMoreStatus } = this.refs;
    if (!loadMoreStatus) return;

    loadMoreStatus.textContent =
      loadMore?.dataset.statusTemplate?.replace('[count]', String(count)) ?? '';
  }

  /**
   * Swaps the Load more control for the one the fetched page rendered. This happens only after the
   * page's buffer is exhausted; until then the existing link still correctly points to that page.
   *
   * @param {Document} parsedPage
   */
  #syncLoadMore(parsedPage) {
    const { loadMore: wrapper, grid } = this.refs;
    const replacement = parsedPage.querySelector('[ref="loadMore"]');

    // No replacement means the fetched page did not render a control at all, which is not the same
    // as a last page (that renders an empty one). Leave the live control alone, do not strip it.
    if (!wrapper || !(replacement instanceof HTMLElement)) return;

    const heldFocus = wrapper.contains(document.activeElement);

    wrapper.replaceChildren(...replacement.childNodes);
    wrapper.dataset.statusTemplate = replacement.dataset.statusTemplate ?? '';

    // replaceChildren destroyed whatever held focus, so put it back: on the replacement while one
    // exists, else on the grid. preventScroll, or focusing the grid jumps them to its first row.
    if (heldFocus) {
      const successor = wrapper.querySelector('a');

      if (successor instanceof HTMLElement) {
        successor.focus({ preventScroll: true });
      } else if (grid) {
        grid.setAttribute('tabindex', '-1');
        grid.focus({ preventScroll: true });
      }
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let parsedPage = this.#parsePage(previousPage.page);
    if (!parsedPage) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      parsedPage = this.#parsePage(previousPage.page);
      if (!parsedPage) return;
    }

    const previousPageItemElements = this.#getGridItems(parsedPage);
    if (!previousPageItemElements) return;

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { grid } = this.refs;
    const isPrevious = type === 'previous';

    // Queried off the grid, not refs.cards: refs refresh a microtask late, so right after an append
    // they still describe the previous page and a second press would re-append the same page.
    const cards = grid?.querySelectorAll(':scope > [ref="cards[]"]');

    if (!cards?.length) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!(targetCard instanceof HTMLElement)) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {Document | undefined}
   */
  #parsePage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    return new DOMParser().parseFromString(pageHTML, 'text/html');
  }

  /**
   * @param {Document} parsedPage
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridItems(parsedPage) {
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(GRID_ITEM_SELECTOR);
  }

  /**
   * Rebuilds the loaded pages and scroll position after a Back or Forward, and only then: a fresh
   * visit or a reload should open at the top of the first page as normal.
   */
  #restorePosition() {
    if (!this.#isBackForwardNavigation()) return;

    const position = this.#readPosition();
    if (!position) return;

    const { grid } = this.refs;
    if (!grid) return;

    // Take over scroll restoration before the pages are back: the browser would restore into a
    // page that no longer holds the products and clamp to the top.
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    // Claim the grid now, before the load-time top-up scheduled alongside this reaches for it.
    this.#loading = true;
    this.refs.loadMore?.setAttribute('aria-busy', 'true');

    this.#restore = (async () => {
      try {
        await this.#appendPagesUpTo(position.page);
      } catch (error) {
        console.error(error);
      } finally {
        this.#loading = false;
        this.refs.loadMore?.removeAttribute('aria-busy');
        this.#restore = null;
        // Square whatever the rebuild left ragged, exactly as a press does when it lets go.
        this.#releaseTailTopUp();
      }

      this.#scrollToPosition(position.y);
    })();
  }

  /**
   * Walks the next-page pipeline forward until the grid holds `targetPage`, so the load-more
   * control and the prefetch end in the same state a shopper who pressed their way there would
   * leave. Whole pages only, so nothing here announces itself or half-fills a row.
   *
   * @param {number} targetPage
   */
  async #appendPagesUpTo(targetPage) {
    const { grid } = this.refs;
    if (!grid || !Number.isFinite(targetPage)) return;

    // A catalogue that has shrunk since the visit caps the walk, so a stale number cannot spin.
    const lastPage = Number(grid.dataset.lastPage) || 1;
    const limit = Math.min(targetPage, lastPage);

    for (let guard = 0; this.#lastLoadedPage() < limit && guard < limit; guard += 1) {
      const before = this.#lastLoadedPage();
      await this.#renderNextPage();
      // No progress means the page dropped; stop rather than ask for it forever.
      if (this.#lastLoadedPage() <= before) break;
    }

    // The control still points at the first page it rendered. Swap in the one the page now on
    // screen carries, so a further press continues correctly and the last page retires it.
    const reached = this.#parsePage(this.#lastLoadedPage());
    if (reached) this.#syncLoadMore(reached);
  }

  /**
   * The page number of the last card in the live grid, or 1 when the grid is empty.
   *
   * @returns {number}
   */
  #lastLoadedPage() {
    const { grid } = this.refs;
    const cards = grid?.querySelectorAll(':scope > [ref="cards[]"]');
    const last = cards?.[cards.length - 1];
    return last instanceof HTMLElement ? Number(last.dataset.page) || 1 : 1;
  }

  /**
   * Scrolls to a remembered offset once the appended rows have been laid out, then hands scroll
   * restoration back to the browser. Manual was only needed to stop it clamping to the top of the
   * not-yet-rebuilt page during this restore; a later same-document traversal (a bfcache resume)
   * restores scroll natively. A no-op scroll at the top of the page, where a first page already sits.
   *
   * @param {number} y
   */
  #scrollToPosition(y) {
    const handBack = () => {
      if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
    };

    if (y <= 0) {
      handBack();
      return;
    }

    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        window.scrollTo({ top: y, behavior: 'instant' });
        handBack();
      })
    );
  }

  /**
   * True when the page was reached by the browser's Back or Forward, the only navigation whose
   * position restored. Shopify pages routinely miss the bfcache, so the navigation entry's type
   * is what tells a rebuilt Back apart from a fresh view; `pageshow`'s `persisted` alone would not.
   *
   * @returns {boolean}
   */
  #isBackForwardNavigation() {
    const entry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    return PaginatedList.#isNavigationTiming(entry) && entry.type === 'back_forward';
  }

  /**
   * `getEntriesByType` types its result as the `PerformanceEntry` base, which has no `type`;
   * `entryType` is the spec's discriminant on that base, so this narrows without a cast.
   *
   * @param {PerformanceEntry | undefined} entry
   * @returns {entry is PerformanceNavigationTiming}
   */
  static #isNavigationTiming(entry) {
    return entry?.entryType === 'navigation';
  }

  /**
   * The sessionStorage key for this list's position: the entry token, plus the section so several
   * lists on one page stay apart.
   *
   * @returns {string}
   */
  #positionKey() {
    return `${POSITION_STORAGE_PREFIX}${this.#entryToken()}:${this.sectionId}`;
  }

  /**
   * What a position is filed under. A real listing navigation carries a per-entry id (stamped on
   * load), so two visits to one URL cannot read or overwrite each other. A transient in-page entry
   * with no stamp of its own falls back to the listing URL, which no state writer can clobber. Read
   * only: the id is stamped separately, in a live context, never while reading.
   *
   * @returns {string}
   */
  #entryToken() {
    const id = this.#entryId();
    if (id) return id;

    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    return `url:${url.pathname}${url.search}`;
  }

  /**
   * The current history entry's stamped id, or null if it carries none.
   *
   * @returns {string | null}
   */
  #entryId() {
    try {
      const id = history.state?.[POSITION_ENTRY_KEY];
      return typeof id === 'string' ? id : null;
    } catch {
      return null;
    }
  }

  /**
   * Stamps a per-entry id into the current `history.state` if it has none, preserving any state the
   * theme's other writers (the cart drawer flag, the facets params) put there. Called on load and
   * on filter, never during pagehide where a history write may not persist.
   */
  #stampEntry() {
    try {
      const state = history.state;
      if (state && typeof state === 'object' && typeof state[POSITION_ENTRY_KEY] === 'string') return;

      const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      const base = state && typeof state === 'object' ? state : {};
      history.replaceState({ ...base, [POSITION_ENTRY_KEY]: id }, '');
    } catch {
      // history is blocked; #entryToken falls back to the listing URL.
    }
  }

  /**
   * Records the last loaded page and the scroll offset as the shopper leaves. `pagehide` fires for
   * a normal unload and a bfcache freeze alike, so the entry is waiting however they come back.
   */
  #savePosition = () => {
    try {
      const page = this.#lastLoadedPage();
      const y = window.scrollY;
      // First page at the top is the default, so clear any snapshot: another visit can reuse this
      // key, and a leftover deep position would wrongly restore on the next Back.
      if (page <= 1 && y <= 0) {
        this.#clearPosition();
        return;
      }
      sessionStorage.setItem(this.#positionKey(), JSON.stringify({ page, y, ts: Date.now() }));
    } catch {
      // sessionStorage can be blocked (private mode, storage disabled); a lost position is fine.
    }
  };

  /**
   * @returns {{ page: number, y: number } | null}
   */
  #readPosition() {
    try {
      const raw = sessionStorage.getItem(this.#positionKey());
      if (!raw) return null;

      const position = JSON.parse(raw);
      if (typeof position?.page !== 'number' || typeof position?.y !== 'number') return null;
      if (Date.now() - (position.ts ?? 0) > POSITION_STORAGE_TTL) {
        this.#clearPosition();
        return null;
      }

      return { page: position.page, y: position.y };
    } catch {
      return null;
    }
  }

  #clearPosition() {
    try {
      sessionStorage.removeItem(this.#positionKey());
    } catch {
      // A sessionStorage that cannot be written cannot be holding a stale entry either.
    }
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    // Filtering pushes a fresh history entry, which carries no position of its own. Stamp it now,
    // while the page is live, so a save before the shopper leaves has an id to key by.
    this.#stampEntry();
    this.#nextPageBuffer = undefined;
    // The filtered results are a new list, so the one-shot retry is re-armed with it, and a page
    // request still in flight for the old one no longer speaks for this one.
    this.#listGeneration += 1;
    this.#nextPageDropped = false;
    this.#tailTopUpRetried = false;

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');

        // Here, not on the event: facets.js dispatches FilterUpdate BEFORE it asks for the section,
        // so squaring the tail there would measure the grid the filter is about to replace.
        if (this.paginationMode === 'load_more') this.#scheduleTailTopUp();
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
